import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';

import { useGetRoles } from '../../hooks/roles/useGetRoles';
import { useDeleteRoles } from '../../hooks/roles/useDeleteRoles';
import { usePermissions } from '../../auth/PermissionsContext';

import ModelShowRole from './ModelShowRole';
import ResourceTableLayout from '../global/Table/TableLayout';
import RolesTableRow from './Table/RolesTableRow';
import RolesTableColumns from './Table/RolesTableColumns';

const RolesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const { has, userRoles, userRolesNames } = usePermissions();
    const { enqueueSnackbar } = useSnackbar();

    const perms = useMemo(
        () => ({
            list: has('roles_list'),
            create: has('roles_create'),
            edit: has('roles_edit'),
            delete: has('roles_delete'),
            view: has('roles_show'),
        }),
        [has]
    );

    const navigate = useNavigate();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [search, setSearch] = useState('');
    const [order, setOrder] = useState('asc');
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);
    const [openView, setOpenView] = useState(false);
    const [viewRoleId, setViewRoleId] = useState(null);

    const filters = useMemo(
        () => ({
            search: search || undefined,
        }),
        [search]
    );

    const { isLoading, error, roles, meta } = useGetRoles(page, perPage, filters);
    const { deleteRoles, isDelete } = useDeleteRoles();

    const sortedRoles = useMemo(() => {
        if (!roles) return [];
        const list = [...roles];
        if (order === 'asc') return list;
        return list.reverse();
    }, [roles, order]);

    const isUserOwnRole = useMemo(() => {
        if (!selectedRole) return false;
        const hasRoleById = userRoles.some(role => role.id === selectedRole.id);
        const hasRoleByName = userRolesNames.includes(selectedRole.name);
        return hasRoleById || hasRoleByName;
    }, [selectedRole, userRoles, userRolesNames]);

    const handleRequestSort = () => {
        setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    };

    const handleMenuOpen = (event, role) => {
        setMenuAnchorEl(event.currentTarget);
        setSelectedRole(role);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedRole(null);
    };

    const handleView = () => {
        if (!perms.view) {
            enqueueSnackbar(isArabic ? 'لا تملك صلاحية العرض' : 'No view permission', { variant: 'error' });
            handleMenuClose();
            return;
        }
        if (selectedRole) {
            setViewRoleId(selectedRole.id);
            setOpenView(true);
        }
        handleMenuClose();
    };

    const handleEdit = () => {
        if (!perms.edit) {
            enqueueSnackbar(isArabic ? 'لا تملك صلاحية التعديل' : 'No edit permission', { variant: 'error' });
            handleMenuClose();
            return;
        }
        if (isUserOwnRole && !userRolesNames.includes('super_admin')) {
            enqueueSnackbar(
                isArabic ? 'لا يمكنك تعديل الدور الخاص بك' : 'You cannot edit your own role',
                { variant: 'error' }
            );
            handleMenuClose();
            return;
        }
        if (selectedRole) {
            navigate(`/roles/${selectedRole.id}/edit`);
        }
        handleMenuClose();
    };

    const handleDelete = () => {
        if (!perms.delete) {
            enqueueSnackbar(isArabic ? 'لا تملك صلاحية الحذف' : 'No delete permission', { variant: 'error' });
            handleMenuClose();
            return;
        }
        if (isUserOwnRole) {
            enqueueSnackbar(
                isArabic ? 'لا يمكنك حذف الدور الخاص بك' : 'You cannot delete your own role',
                { variant: 'error' }
            );
            handleMenuClose();
            return;
        }
        if (selectedRole) {
            deleteRoles(selectedRole.id);
        }
        handleMenuClose();
    };

    const handleAddClick = () => {
        if (!perms.create) {
            enqueueSnackbar(isArabic ? 'لا تملك صلاحية الإنشاء' : 'No create permission', { variant: 'error' });
            return;
        }
        navigate('/roles/create');
    };

    const handlePageChange = (value) => setPage(value);

    const handlePerPageChange = (value) => {
        setPerPage(value);
        setPage(1);
    };

    const checkIfUserRole = (roleItem) => {
        const hasById = userRoles.some(role => role.id === roleItem.id);
        const hasByName = userRolesNames.includes(roleItem.name);
        return hasById || hasByName;
    };

    const renderRow = (params) => (
        <RolesTableRow
            key={params.key || params.item.id}
            role={params.item}
            index={params.index}
            isArabic={params.isArabic}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            order={params.order}
            totalItems={sortedRoles.length}
            onMenuOpen={handleMenuOpen}
            menuDisabled={!perms.edit && !perms.delete && !perms.view}
            isUserOwnRole={checkIfUserRole(params.item)}
        />
    );

    return (
        <>
            <ResourceTableLayout
                title={isArabic ? 'الأدوار' : 'Roles'}
                subtitle={
                    isArabic
                        ? 'إدارة الأدوار والصلاحيات في النظام'
                        : 'Manage system roles and permissions'
                }
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إنشاء دور جديد' : 'Create new role'}
                onAddClick={handleAddClick}
                addButtonDisabled={!perms.create}
                addButtonTooltip={
                    !perms.create
                        ? isArabic
                            ? 'لا تملك صلاحية الإنشاء'
                            : 'No create permission'
                        : undefined
                }
                isLoading={isLoading}
                error={error}
                items={sortedRoles}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                order={order}
                onRequestSort={handleRequestSort}
                columns={RolesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuView={perms.view ? handleView : undefined}
                onMenuEdit={perms.edit ? handleEdit : undefined}
                onMenuDelete={perms.delete ? handleDelete : undefined}
                menuDisableView={!perms.view}
                menuDisableEdit={!perms.edit || (isUserOwnRole && !userRolesNames.includes('super_admin'))}
                menuDisableDelete={!perms.delete}
                isDeletingSingle={isDelete}
                emptyLabelAr="لا توجد أدوار"
                emptyLabelEn="No roles"
                loadErrorAr="حدث خطأ أثناء تحميل الأدوار"
                loadErrorEn="Failed to load roles"
            />

            {openView && (
                <ModelShowRole
                    roleId={viewRoleId}
                    open={openView}
                    onClose={() => setOpenView(false)}
                />
            )}
        </>
    );
};

export default RolesTable;
